import React, { FC, useCallback, useEffect, useState } from 'react';
import { Button, Flex } from '@chakra-ui/react';

import { useAlert, useApi } from '@app/hooks';
import { TableBase } from '@app/components/common/table/table-base';
import Dialog from '@app/components/common/modals/dialog';
import { SwitchField, TextField } from '@app/components/common';
import { CategorySku } from '@app/api/models/sku';

import { useColumns } from './columns';

type CategoryDraft = Partial<CategorySku>;

export const SkuCategoriesTable: FC = () => {
  const api = useApi();
  const { error } = useAlert();

  const [categories, setCategories] = useState<CategorySku[]>([]);
  const [loading, setLoading] = useState(false);
  const [current, setCurrent] = useState<CategoryDraft>();
  const [currentDialog, setCurrentDialog] = useState<'edit' | 'create'>();

  const fetchList = useCallback(() => {
    setLoading(true);
    api.sku.getCategories().then(({ data }) => {
      setLoading(false);
      setCategories(data || []);
    });
  }, [api]);

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onEdit = (category: CategorySku) => {
    setCurrent(category);
    setCurrentDialog('edit');
  };

  const columns = useColumns({ onEdit });

  const onClose = () => {
    setCurrent(undefined);
    setCurrentDialog(undefined);
  };

  const onSubmit = (category?: CategoryDraft) => async () => {
    if (!category?.name) {
      error('Введите название');
      return;
    }
    setLoading(true);
    if (category.id) {
      await api.sku.updateCategory({
        id: category.id,
        name: category.name,
        is_active: !!category.is_active,
      });
    } else {
      await api.sku.createCategory({
        name: category.name,
        is_active: category.is_active ?? true,
      });
    }
    fetchList();
    onClose();
  };

  return (
    <>
      <TableBase<CategorySku>
        title="Категории SKU"
        data={categories}
        columns={columns}
        loading={loading}
        onCreateClick={() => {
          setCurrent({ is_active: true });
          setCurrentDialog('create');
        }}
      />
      <Dialog
        open={!!currentDialog}
        onClose={onClose}
        title={currentDialog === 'edit' ? 'Редактирование категории' : 'Создание категории'}
      >
        <Flex gap={2} flexDirection="column">
          <TextField
            id="name"
            label="Название"
            required
            value={current?.name || ''}
            onChange={(value) =>
              setCurrent({
                ...current,
                name: value,
              })
            }
          />
          <SwitchField
            label="Активна"
            value={current?.is_active}
            onChange={(value) =>
              setCurrent({
                ...current,
                is_active: value,
              })
            }
            id="is_active"
          />
          <Flex justifyContent="flex-end" gap={2}>
            <Button variant="outline" colorScheme="gray" onClick={onClose}>
              Отмена
            </Button>
            <Button variant="solid" colorScheme="brand" onClick={onSubmit(current)}>
              Сохранить
            </Button>
          </Flex>
        </Flex>
      </Dialog>
    </>
  );
};

export default SkuCategoriesTable;
